import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrService } from 'ngx-toastr';

const C11N_VIDEO_PLAYER_SRC =
  'https://cdn.web.vanguard.com/c11n-proto/b5/video-player.js';
const WISTIA_MEDIA_BASE_URL = 'https://myclassroomeconomy.wistia.com/medias';
const WISTIA_OEMBED_URL = 'https://fast.wistia.com/oembed';
const DEFAULT_VIDEO_THUMBNAIL = './assets/images/websitelogo_xs-md.svg';

interface WistiaOEmbedResponse {
  thumbnail_url: string;
}

// Minimal interfaces for lesson content structure
interface LessonContent {
  _path: string;
  title: string;
  totalPages: number;
  pages: LessonPage[];
}

interface LessonPage {
  pageNumber: number;
  pageTitle: string;
  content: {
    paragraph: { html: string };
    additionalParagraph?: { html: string };
    keyTerms?: {
      heading: string;
      description: string;
      terms: Array<{ term: string; definition: string }>;
    };
    activities?: {
      activityTitle?: string;
      activityDescription?: { html: string };
      activityItems?: Array<{
        itemTitle?: string;
        itemDescription?: { html: string };
      }>;
    };
  };
}

@Component({
  selector: 'app-learning-content',
  templateUrl: './learning-content.component.html',
  styleUrls: ['./learning-content.component.scss'],
  standalone: false,
})
export class LearningContentComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() lessonId?: string; // For mock data compatibility
  @Input() lessonPath?: string; // For API integration
  @Input() userRole?: 'student' | 'teacher' = 'student';
  @ViewChild('backToLibraryBtn', { read: ElementRef })
  backToLibraryBtn?: ElementRef<HTMLElement>;
  @ViewChild('videoPlayerTemplate', { static: true, read: ElementRef })
  videoPlayerTemplate?: ElementRef<HTMLTemplateElement>;
  @ViewChild('lessonContentContainer', { static: true, read: ElementRef })
  lessonContentContainer?: ElementRef<HTMLElement>;
  @Output() backToLibrary = new EventEmitter<void>();
  @Output() goToQuiz = new EventEmitter<{
    lessonId: string;
    lessonTitle: string;
    quizData?: any;
  }>();

  private readonly _destroyed$ = new Subject<void>();

  lessonContent?: LessonContent;
  currentPage: LessonPage | undefined;
  currentPageNumber: number = 1;
  safeHtmlContent?: SafeHtml;
  additionalParagraphContent?: SafeHtml;
  isXsScreen: boolean = false;
  showKeyTermsAccordion: boolean = true;
  isLargeScreen: boolean = true;
  quizContentReference: any = null; // Store quiz data from lesson API
  pageAnnouncement: string = '';
  isLoadingLesson: boolean = false;

  private _pendingTimers: ReturnType<typeof setTimeout>[] = [];

  constructor(
    private sanitizer: DomSanitizer,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private elementRef: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private translate: TranslateService,
    private readonly toast: ToastrService,
  ) {}

  private onResize = () => {
    this.checkScreenSize();
  };

  private checkScreenSize() {
    if (!isPlatformBrowser(this.platformId)) return;
    const width = window.innerWidth;
    this.isXsScreen = width < 576;
    this.isLargeScreen = width >= 1200; // Large screens don't need accordion
  }
  ngOnInit() {
    this.checkScreenSize();
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('resize', this.onResize);
    }
    this.loadVideoPlayerComponent();
    this.loadLessonContent();
  }

  ngAfterViewInit() {
    const timer = setTimeout(() => {
      this.backToLibraryBtn?.nativeElement?.focus();
    }, 0);
    this._pendingTimers.push(timer);
  }
  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.onResize);
    }
    this._pendingTimers.forEach((t) => clearTimeout(t));
    this._pendingTimers = [];
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  toggleKeyTermsAccordion() {
    this.showKeyTermsAccordion = !this.showKeyTermsAccordion;
  }

  loadLessonContent() {
    if (this.lessonPath) {
      this.fetchLessonFromAPI();
    }
  }

  private loadVideoPlayerComponent(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.document.querySelector(`script[src="${C11N_VIDEO_PLAYER_SRC}"]`))
      return;

    const script = this.document.createElement('script');
    script.type = 'module';
    script.src = C11N_VIDEO_PLAYER_SRC;
    this.document.head.appendChild(script);
  }

  /**
   * Fetch lesson content from the API
   * NOTE: Currently used for teacher viewing lessons from AEM
   */
  private fetchLessonFromAPI(): void {
    if (!this.lessonPath) return;

    this.isLoadingLesson = true;
    this.apiService
      .getLessonContent(this.lessonPath)
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: (response) => {
          this.isLoadingLesson = false;
          const lessonData =
            Array.isArray(response) && response.length > 0
              ? response[0]
              : response;

          // Extract quiz data from the lesson response
          if (lessonData.quizContentReference) {
            this.quizContentReference = lessonData.quizContentReference;
          }

          if (
            lessonData &&
            lessonData.lessonPages &&
            lessonData.lessonPages.length > 0
          ) {
            this.lessonContent = {
              _path: this.lessonPath || '',
              title:
                lessonData.learningTitle ||
                lessonData.quizTitle ||
                'Untitled Lesson',
              totalPages: lessonData.lessonPages.length,
              pages: lessonData.lessonPages.map((page: any, index: number) => {
                // Process HTML to convert relative AEM image paths to full publish URLs
                // Images are embedded in HTML like: <img src="/content/dam/...">
                const processedHtml = this.ensureImageAltText(
                  this.processVideoEmbeds(
                    this.processAemImageUrls(
                      page.pageContent?.html || page.pageContent || ''
                    )
                  )
                );

                return {
                  pageNumber: page.pageNumber || index + 1,
                  pageTitle:
                    page.lessonTitle ||
                    lessonData.learningTitle ||
                    `Page ${index + 1}`,
                  content: {
                    paragraph: {
                      html: processedHtml,
                    },
                    keyTerms: this.parseKeyTerms(
                      page.termDefinitions?.html || page.termDefinitions || ''
                    ),
                  },
                };
              }),
            };

            this.loadPage(1);
          } else {
            this.showToastrMsg(
              'learningLibrary.error_messages.lesson_not_available',
              '',
              'error'
            );
          }
        },
        error: () => {
          this.isLoadingLesson = false;
          this.showToastrMsg('learningLibrary.error_messages.failed_load_lesson', '', 'error');
        },
      });
  }

  showToastrMsg(header: string, description: string, type: string) {
    return this.toast.show(description, header, { toastClass: type });
  }

  /**
   * Process HTML to convert relative AEM image paths to full URLs.
   * Images embedded in AEM HTML arrive as: <img src="/content/dam/...">
   * We prepend aemImageBaseUrl from environment (env-specific, never hardcoded).
   */
  private processAemImageUrls(html: string): string {
    if (!html || typeof html !== 'string') return '';

    const baseUrl = (environment as any).aemImageBaseUrl || '';

    return html.replace(
      /(<img[^>]+src=["'])(\/(content\/dam\/[^"']+))(["'])/gi,
      `$1${baseUrl}$2$4`
    );
  }

  private ensureImageAltText(html: string): string {
    if (!html || typeof html !== 'string') return '';
    if (!isPlatformBrowser(this.platformId)) return html;
    if (html.indexOf('<img') === -1) return html;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const imagesMissingAlt = doc.querySelectorAll('img:not([alt])');
    if (imagesMissingAlt.length === 0) return html;

    imagesMissingAlt.forEach((img) => {
      const assetRef = img.getAttribute('data-assetref')?.trim();
      img.setAttribute('alt', assetRef || '');
    });

    return doc.body.innerHTML;
  }

  private processVideoEmbeds(html: string): string {
    if (!html || typeof html !== 'string') return '';
    if (!isPlatformBrowser(this.platformId)) return html;
    if (html.indexOf('video-embed') === -1) return html;

    const templateMarkup =
      this.videoPlayerTemplate?.nativeElement?.innerHTML?.trim() || '';
    if (!templateMarkup) return html;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const embeds = doc.querySelectorAll('div.video-embed');

    embeds.forEach((embed) => {
      const videoId = embed.getAttribute('data-video-id') || '';
      const title = embed.getAttribute('data-title') || '';

      if (!videoId) return;
      const wrapper = doc.createElement('div');
      wrapper.innerHTML = templateMarkup;

      const player = wrapper.querySelector('c11n-proto-b5-video-player');
      player?.setAttribute('video-id', videoId);
      player?.setAttribute('title', title);

      const img = wrapper.querySelector('img');
      img?.setAttribute('alt', title);
      img?.setAttribute('src', DEFAULT_VIDEO_THUMBNAIL);
      img?.setAttribute('data-wistia-id', videoId);

      const playButton = wrapper.querySelector('button');
      playButton?.setAttribute(
        'aria-label',
        this.translate.instant('studentLearningQuiz.video_play_button', {
          title,
        })
      );

      embed.replaceWith(...Array.from(wrapper.childNodes));
    });

    return doc.body.innerHTML;
  }

  private resolveVideoThumbnails(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const images =
      this.elementRef.nativeElement.querySelectorAll<HTMLImageElement>(
        'img[data-wistia-id]'
      );

    images.forEach((img) => {
      if (img.dataset['wistiaResolved'] === 'true') return;
      const videoId = img.getAttribute('data-wistia-id');
      if (!videoId) return;
      img.dataset['wistiaResolved'] = 'true';

      const oEmbedUrl = `${WISTIA_OEMBED_URL}?url=${WISTIA_MEDIA_BASE_URL}/${encodeURIComponent(
        videoId
      )}`;

      this.http
        .get<WistiaOEmbedResponse>(oEmbedUrl)
        .pipe(
          takeUntil(this._destroyed$),
          catchError(() => of(null))
        )
        .subscribe((response) => {
          if (response?.thumbnail_url) {
            img.setAttribute('src', response.thumbnail_url);
          }
        });
    });
  }

  /**
   * Parse HTML key terms into structured format
   * Converts "<p><b>Term:</b> definition</p>" format to {term, definition} array
   */
  private parseKeyTerms(html: string):
    | {
        heading: string;
        description: string;
        terms: Array<{ term: string; definition: string }>;
      }
    | undefined {
    if (!html || typeof html !== 'string' || html.trim() === '')
      return undefined;
    if (!isPlatformBrowser(this.platformId)) return undefined;

    const terms: Array<{ term: string; definition: string }> = [];

    // Parse HTML to extract terms and definitions
    // Format: <p><b>Term:</b> definition</p>
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const paragraphs = doc.querySelectorAll('p');

    paragraphs.forEach((p) => {
      const boldElement = p.querySelector('b');
      if (boldElement) {
        const term = boldElement.textContent?.replace(':', '').trim() || '';
        const definition =
          p.textContent?.replace(boldElement.textContent || '', '').trim() ||
          '';

        if (term && definition) {
          terms.push({ term, definition });
        }
      }
    });

    if (terms.length === 0) return undefined;

    return {
      heading: 'Key Terms',
      description: "Vocabulary you'll unlock after completing this lesson.",
      terms,
    };
  }

  loadPage(pageNumber: number) {
    if (this.lessonContent) {
      this.currentPage = this.lessonContent.pages.find(
        (p) => p.pageNumber === pageNumber
      );
      this.currentPageNumber = pageNumber;
      this.pageAnnouncement = this.translate.instant(
        'studentLearningQuiz.lesson_progress_aria',
        { current: pageNumber, total: this.lessonContent.totalPages }
      );

      // Use bypassSecurityTrustHtml to allow iframe embeds (for videos)
      // This is safe because content comes from trusted AEM source or mock data
      if (this.currentPage?.content?.paragraph?.html) {
        this.safeHtmlContent = this.sanitizer.bypassSecurityTrustHtml(
          this.currentPage.content.paragraph.html
        );
      }

      if (this.currentPage?.content?.additionalParagraph?.html) {
        this.additionalParagraphContent =
          this.sanitizer.bypassSecurityTrustHtml(
            this.currentPage.content.additionalParagraph.html
          );
      }

      // Scroll so the library tabs stay at the top, not the whole page.
      this.scrollLessonContentToTop();
      this.cdr.detectChanges();
      if (isPlatformBrowser(this.platformId)) {
        const timer = setTimeout(() => this.resolveVideoThumbnails(), 0);
        this._pendingTimers.push(timer);
      }
    }
  }

  /**
   * Scroll the learning-library container into view (keeping its tabs at the top)
   * instead of the whole window. Falls back to the content area if not found.
   */
  private scrollLessonContentToTop(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const container = this.lessonContentContainer?.nativeElement;
    if (!container) {
      // Fallback: ensure we still scroll to the top (matches the previous
      // window.scrollTo behavior) if the container isn't resolved yet.
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    // Prefer the library container so its tabs remain visible at the top.
    const libraryHost =
      container.closest('app-student-learning-library') ??
      container.closest('app-learning-library');
    const target = (libraryHost as HTMLElement | null) ?? container;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  isNextButtonDisabled(): boolean {
    if (this.userRole === 'teacher') {
      return (
        !this.lessonContent ||
        this.currentPageNumber >= this.lessonContent.totalPages ||
        this.lessonContent.totalPages === 1
      );
    }
    return false;
  }

  nextPage() {
    if (
      this.lessonContent &&
      this.currentPageNumber < this.lessonContent.totalPages
    ) {
      this.loadPage(this.currentPageNumber + 1);
      this.backToLibraryBtn?.nativeElement?.focus();
    } else if (
      this.lessonContent &&
      this.currentPageNumber === this.lessonContent.totalPages &&
      this.userRole === 'student'
    ) {
      this.navigateToQuiz();
    }
  }

  previousPage() {
    if (this.currentPageNumber > 1) {
      this.loadPage(this.currentPageNumber - 1);
      this.backToLibraryBtn?.nativeElement?.focus();
    }
  }

  navigateToQuiz() {
    if (this.lessonContent && this.lessonId) {
      this.goToQuiz.emit({
        lessonId: this.lessonId,
        lessonTitle: this.lessonContent.title,
        quizData: this.quizContentReference,
      });
    }
  }

  goBack() {
    this.backToLibrary.emit();
  }
}
